import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Bot, Loader2, Sparkles, Copy, CheckCircle2, Plus,
  Linkedin, Facebook, Instagram, X, Save, ChevronDown
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const TONES = [
  { value: "professionnel", label: "Professionnel" },
  { value: "decontracte", label: "Decontracte" },
  { value: "inspirant", label: "Inspirant" },
  { value: "educatif", label: "Educatif" },
  { value: "promotionnel", label: "Promotionnel" },
];

export function MarketingPostGenerator({ api, onPostSaved, templates = [] }) {
  const [prompt, setPrompt] = useState("");
  const [platforms, setPlatforms] = useState(["linkedin"]);
  const [tone, setTone] = useState("professionnel");
  const [templateId, setTemplateId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [savingIdx, setSavingIdx] = useState(null);
  const [showSchedule, setShowSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");

  const togglePlatform = (p) => {
    setPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const generate = async () => {
    if (!prompt.trim() && !templateId) return;
    setGenerating(true);
    setResults([]);
    try {
      const resp = await api.post("/marketing/generate-post", {
        prompt: prompt.trim(),
        platforms,
        tone,
        template_id: templateId || undefined,
      });
      setResults(resp.data.generated || []);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de generation");
    }
    setGenerating(false);
  };

  const copyToClipboard = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const saveAsPost = async (item, idx) => {
    setSavingIdx(idx);
    try {
      await api.post("/marketing/posts", {
        platforms: [item.platform],
        content: item.content,
        hashtags: item.hashtags || [],
        scheduled_at: scheduleDate || "",
        ai_generated: true,
        prompt_used: prompt,
      });
      if (onPostSaved) onPostSaved();
      setShowSchedule(null);
      setScheduleDate("");
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
    setSavingIdx(null);
  };

  const saveAllPosts = async () => {
    for (let i = 0; i < results.length; i++) {
      await saveAsPost(results[i], i);
    }
  };

  return (
    <div data-testid="post-generator" className="space-y-5">
      {/* Prompt area */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-[#10b981]/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#10b981]" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Generateur de posts IA</h3>
            <p className="text-[10px] text-slate-500">Decrivez votre idee, l'IA genere le contenu adapte a chaque plateforme</p>
          </div>
        </div>

        {/* Template selector */}
        {templates.length > 0 && (
          <div className="mb-3">
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Template de prompt</label>
            <select
              value={templateId}
              onChange={e => setTemplateId(e.target.value)}
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs"
              data-testid="template-selector"
            >
              <option value="">Aucun template</option>
              {templates.map(t => (
                <option key={t.template_id} value={t.template_id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Prompt */}
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="Ex: Annonce de notre nouveau service d'accompagnement fiscal pour les startups, ton enthousiaste, mentionner les avantages cles..."
          className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-4 py-3 text-sm h-28 resize-none placeholder:text-slate-600 focus:border-[#10b981]/40 focus:outline-none transition-colors"
          data-testid="ai-prompt-input"
        />

        {/* Platform toggles */}
        <div className="flex flex-wrap gap-2 mt-3">
          <label className="text-[10px] text-slate-500 uppercase self-center mr-1">Plateformes :</label>
          {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
            const Icon = cfg.icon;
            const active = platforms.includes(k);
            return (
              <button
                key={k}
                onClick={() => togglePlatform(k)}
                data-testid={`platform-toggle-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  active
                    ? "text-white shadow-sm"
                    : "bg-[#0b0811] text-slate-500 border border-white/5 hover:border-white/10"
                }`}
                style={active ? { background: cfg.color } : {}}
              >
                <Icon className="w-3.5 h-3.5" /> {cfg.label}
              </button>
            );
          })}
        </div>

        {/* Tone */}
        <div className="flex flex-wrap gap-2 mt-3">
          <label className="text-[10px] text-slate-500 uppercase self-center mr-1">Ton :</label>
          {TONES.map(t => (
            <button
              key={t.value}
              onClick={() => setTone(t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                tone === t.value
                  ? "bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30"
                  : "bg-[#0b0811] text-slate-500 border border-white/5 hover:border-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Generate button */}
        <Button
          onClick={generate}
          disabled={generating || (!prompt.trim() && !templateId) || platforms.length === 0}
          className="mt-4 bg-[#10b981] text-white hover:bg-[#059669] font-bold w-full sm:w-auto"
          data-testid="generate-posts-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation en cours...</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" /> Generer les posts</>
          )}
        </Button>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Bot className="w-4 h-4 text-[#10b981]" />
              {results.length} post{results.length > 1 ? "s" : ""} genere{results.length > 1 ? "s" : ""}
            </h3>
            {results.length > 1 && (
              <Button
                onClick={saveAllPosts}
                size="sm"
                className="bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981]/20 text-xs"
                data-testid="save-all-posts-btn"
              >
                <Save className="w-3 h-3 mr-1" /> Sauvegarder tous
              </Button>
            )}
          </div>

          {results.map((item, idx) => {
            const cfg = PLATFORM_CONFIG[item.platform] || PLATFORM_CONFIG.linkedin;
            const Icon = cfg.icon;
            return (
              <div
                key={idx}
                className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
                data-testid={`generated-post-${idx}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${cfg.color}15` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    </div>
                    <span className="text-xs font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981]">IA</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => copyToClipboard(item.content, idx)}
                      className="p-1.5 hover:bg-white/5 rounded-lg transition-colors"
                      title="Copier"
                    >
                      {copiedIdx === idx ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#10b981]" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-slate-400" />
                      )}
                    </button>
                    <button
                      onClick={() => { setShowSchedule(idx); setScheduleDate(""); }}
                      className="p-1.5 hover:bg-[#10b981]/10 rounded-lg transition-colors"
                      title="Sauvegarder comme post"
                      data-testid={`save-post-${idx}`}
                    >
                      {savingIdx === idx ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#10b981]" />
                      ) : (
                        <Plus className="w-3.5 h-3.5 text-[#10b981]" />
                      )}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{item.content}</p>

                {item.hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.hashtags.map((h, i) => (
                      <span key={i} className="text-[9px] text-[#10b981]">#{h}</span>
                    ))}
                  </div>
                )}

                {/* Schedule popover */}
                {showSchedule === idx && (
                  <div className="mt-3 p-3 bg-[#0b0811] border border-white/10 rounded-lg">
                    <label className="text-[10px] text-slate-500 uppercase block mb-1">Planifier (optionnel)</label>
                    <Input
                      type="datetime-local"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      className="bg-[#14101c] border-white/10 text-white text-xs mb-2"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => saveAsPost(item, idx)}
                        size="sm"
                        className="bg-[#10b981] text-white text-xs flex-1"
                        disabled={savingIdx === idx}
                        data-testid={`confirm-save-post-${idx}`}
                      >
                        {savingIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        {scheduleDate ? "Planifier" : "Sauvegarder brouillon"}
                      </Button>
                      <Button
                        onClick={() => setShowSchedule(null)}
                        size="sm"
                        variant="outline"
                        className="border-white/10 text-slate-400 text-xs"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
